import React, { useEffect, useState } from 'react';
import InitialInformation from './InitialInformation';
import MerchantInfo from './MerchantInfo';
import PricingInfoForm from './PricingInfoForm';
import FinalizeAndEmail from './FinalizeAndEmail';
import MonitorApplication from './MonitorApplication';
import NexioInformation from './NexioInformation';
import FinalApproval from './FinalApproval';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import {Stepper, StepLabel, Step, StepContent, Paper, makeStyles, Grid, Typography, CircularProgress, Backdrop, Button} from '@material-ui/core';
import PricingInfo from './PricingInfoType';

const useStyles = makeStyles(theme => ({
    root: {
        padding: "15px",
    },
    longTextField: {
        minWidth: "250px"
    },
    select: {
        minWidth: "120px"
    },
    backdrop: {
        zIndex: 10000,
        backgroundColor: "rgba(0, 0, 0, 0.3)"
    },
    accordionOdd: {
        backgroundColor: theme.palette.primary.main + "1f",
        borderRadius: "15px"
    },
    accordionEven: {
        backgroundColor: theme.palette.secondary.main + "1f",
        borderRadius: "15px"
    }
}))

export default function Onboarding() {
    // Global variables
    const history = useHistory();
    const { vendorId } = useParams();
    const classes = useStyles();
    const [step, setStep] = useState(0);
    const [vendorExists, setVendorExists] = useState(false);
    const [vendorQueried, setVendorQueried] = useState(false);
    const [loadingInfo, setLoadingInfo] = useState(false);
    // This is to prevent the edge case of the vendor status from going backwards (updating fees after onboarding is complete)
    const [vendorStatus, setVendorStatus] = useState("");
    
    // Step 1 variable
    const [initialInformation, setInitialInformation] = useState({name: "", cdyxTenant: "", ppayVendor: ""});
    
    // Step 2 variables
    const [merchantInfo, setMerchantInfo] = useState({});
    const [cmsInfoGenerated, setCMSInfoGenerated] = useState(false);
    const [cmsInfoSaved, setCmsInfoSaved] = useState(false);
    
    /**
     * Questions:
     *      1) VMD and Amex have a Tiered parent column (in yellow). Should the user only be allowed to select one of the three options (Qualified, Mid qualified, Non qualified)? Should there be a currency or percentage to apply to whichever one is selected? The Google Docs document that I was sent has those fields as a number.
*               a) Those are always included and are percentages. 
     *      2) Same as #1 but with the ERR parent column. 
*               a) Always included and are percentages.
     *      3) For Interchange, the cell says "IC Rate or Flat Rate". Should that be split into two radio buttons? Should there be an accompanying textfield for a percentage input?
     *          a) Flat = Interchange (but separate). interchangeRate and flatRate are field names. Types of inputs are identical. 
     *      4) What is the data type of the "Per Pin - Debit" cell?
     *          a) Currency
     *      5) The pricing types that were specified are Tiered, ERR, Interchange, and Flat. Does this hold with this new diagram? Where do "Per Transaction" and "Per PIN - Debit" go?
     *          a) These apply to each of the pricing types. Keep them individual to VMD and AMEX.
     *      6) The "Fees (All Cards)"" group includes a lot of different cells. Are all of these strictly going to be currency? Even for ones like "Help Desk" and "Voice Authorization"?
     *          a) Yes these are currency.
     *      7) Should we allow the VMD group to be disabled just like Amex since we have a separate group for all cards?
     *          a) VMD is permanently on. 
     *      8) What do the little 'xx's mean below each of the cells in the top two groups?
     *          a) It is a decimal with two decimals of precision.
     *      9) What should these fields be called when we stringify the data?
     *          a) Capital camel case for now
     *      10) Should we even continue to allow the specification of the pricing type? Or should the user just fill in every field anyways?
     *          a) Keep the pricing the same: ERR, Interchange, Tiered, Flat
     * 
     *      ID and merchantAppId are auto-generated from step 2. 
     *      Right now, they are BOTH populated with the merchantId from step two.
     * ---------------------------------
     *      Missing info:
     *          nonQualifiedRateErr: min
     *          perTransactionFee: min (could be $0.10)
     *          perDebitFee: min
     *          pciComplianceFee: min
     *          minimumMonthlyFee: min (could be $10.99)
     *          accountServiceFee: min
     *          monthlyRegulatoryFee: min
     *          chargebackFee: min
     *          retrievalFee: min
     *          nsfFee: min
     *          edcAvsFee: min
     *          voiceAuthorizationFee: min
     *          helpdeskFee: min
     * 
     *      Ultimately, names of each of the fields may end up changing. Half of everything specified here is a wild guess.
     */

    // Step 3 variable
    const [pricingInfo, setPricingInfo] = useState({data: new PricingInfo()});

    // Step 4 variable
    const [applicationUpdated, setApplicationUpdated] = useState(false);

    // Step 5 variable
    const [applicationCompleted, setApplicationCompleted] = useState(false);
    
    // Step 6 variable
    const [nexioTokenGenerated, setNexioTokenGenerated] = useState(false);

    // Step 7 variable
    const [finalApproval, setFinalApproval] = useState(false);

    useEffect(() => {
        async function getData() {
            setLoadingInfo(true);
            try {
                const vendorData = await axios.get(`${process.env.REACT_APP_PP_URL}/cms-onboard/get-vendor/` + vendorId, { headers: { 'x-api-key': process.env.REACT_APP_PP_API_KEY}}).catch(err => {console.log("No vendor found: ", err);});
                if(vendorData && vendorData.data) {
                    // Set the vendor initial info
                    setVendorStatus(vendorData.data.status);
                    setInitialInformation({name: vendorData.data.name, cdyxTenant: "None", ppayVendor: vendorData.data.id});
                    setMerchantInfo(vendorData.data.cmsInfo);
                    let pendingCMSInfo = false;
                    if(vendorData.data.cmsInfo.cmsMerchId === "" || vendorData.data.cmsInfo.cmsMerchHash === "" || vendorData.data.cmsInfo.cmsAppToken === "") {
                        setCMSInfoGenerated(false);
                        setStep(1);
                        pendingCMSInfo = true;
                    } else {
                        setCMSInfoGenerated(true);
                        setStep(2);
                        setCmsInfoSaved(true);
                    }

                    // See if there are any saved pricing informations
                    await axios.get(`${process.env.REACT_APP_PP_URL}/cms-onboard/get-fees/` + vendorId, {headers: {'x-api-key': process.env.REACT_APP_PP_API_KEY}})
                        .then(response => {
                            // If we get a 2xx, then proceed to parse the pricing info.
                            const data = response.data.items[0];

                            pricingInfo.data.dynamoRowId = data.id;
                            pricingInfo.data.selectedPricingType = data.type;

                            pricingInfo.data.ParseFieldJsonString(data.vmdPricing, "vmdPricing");
                            pricingInfo.data.ParseFieldJsonString(data.amexPricing, "amexPricing");
                            pricingInfo.data.ParseFieldJsonString(data.fees, "fees");

                            setPricingInfo({...pricingInfo});

                            if(!pendingCMSInfo) {
                                setStep(3);
                            
                                if (vendorData.data.status === "ApplicationUpdated") {
                                    setApplicationUpdated(true);
                                    setStep(4);
                                } else if (vendorData.data.status === "OnBoardComplete") {
                                    setNexioTokenGenerated(true);
                                    setApplicationUpdated(true);
                                    setApplicationCompleted(true);
                                    setStep(6);
                                }
                            }
                        }).catch(error => {
                            // If we get a 404, then there is no pricing info saved
                            if(error.response.status === 404 && !pendingCMSInfo) {
                                setStep(2);
                            }
                        });
                    setVendorExists(true);
                } else {
                    setVendorExists(false);
                }
                setVendorQueried(true);
                setLoadingInfo(false);
            } catch (err) {
                console.log(err);
            }
        }

        if(vendorId) {
            if(vendorId !== "new") {
                getData();
            } else {
                setVendorQueried(true);
                setVendorExists(true);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    function unsafeAdvanceStep() {
        setStep(step + 1);
    }

    function unsafeStepBack() {
        setStep(step - 1);
    }
    
    return (
        <div>
            <Grid container spacing={2} direction="column">
                <Grid container item direction='row' justify='space-between'>
                    <Grid item>
                        <Typography variant="h4">Onboarding</Typography>
                    </Grid>
                    <Grid item>
                        <Button onClick={() => history.push('/admin/onboarding/')} variant='contained' color='primary'>Close</Button>
                    </Grid>
                </Grid>
                <Grid item>
                    {!vendorExists && vendorQueried && 
                    <Typography variant='h6'>Vendor <i>{vendorId}</i> does not exist.</Typography>}
                    {vendorExists && vendorQueried && 
                    <Paper className={classes.root}>   
                        <Stepper orientation="vertical" activeStep={step}>
                            <Step>
                                <StepLabel>Initial Information</StepLabel>
                                <StepContent>
                                    <InitialInformation 
                                        vendorInURL={vendorId}
                                        initialInformation={initialInformation}
                                        setInitialInformation={setInitialInformation}
                                        nextStep={unsafeAdvanceStep}
                                        classes={classes}
                                        setVendorStatus={setVendorStatus}
                                    />
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel>Merchant Information</StepLabel>
                                <StepContent>
                                    <MerchantInfo
                                        initialInformation={initialInformation}
                                        nextStep={unsafeAdvanceStep}
                                        stepBack={unsafeStepBack}
                                        merchantInfo={merchantInfo}
                                        setMerchantInfo={setMerchantInfo}
                                        cmsInfoGenerated={cmsInfoGenerated}
                                        setCMSInfoGenerated={setCMSInfoGenerated}
                                        cmsInfoSaved={cmsInfoSaved}
                                        setCmsInfoSaved={setCmsInfoSaved}
                                    />
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel>Pricing Information</StepLabel>
                                <StepContent>
                                    <PricingInfoForm
                                        pricingInfo={pricingInfo}
                                        setPricingInfo={setPricingInfo}
                                        merchantInfo={merchantInfo}
                                        vendorId={initialInformation.ppayVendor}
                                        classes={classes}
                                        stepBack={unsafeStepBack}
                                        nextStep={unsafeAdvanceStep}
                                    />
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel>{"Finalize & Email Application"}</StepLabel>
                                <StepContent>
                                    <FinalizeAndEmail 
                                        stepBack={unsafeStepBack}
                                        nextStep={unsafeAdvanceStep}
                                        applicationUpdated={applicationUpdated}
                                        setApplicationUpdated={setApplicationUpdated}
                                        vendorInfo={initialInformation}
                                        cmsInfo={merchantInfo}
                                        vendorStatus={vendorStatus}
                                        pricingInfo={pricingInfo}
                                    />
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel>Monitor Application</StepLabel>
                                <StepContent>
                                    <MonitorApplication 
                                        stepBack={unsafeStepBack}
                                        nextStep={unsafeAdvanceStep}
                                        appComplete={applicationCompleted}
                                        setAppComplete={setApplicationCompleted}
                                        cmsMerchHash={merchantInfo.cmsMerchHash}
                                    />
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel>Nexio Token</StepLabel>
                                <StepContent>
                                    <NexioInformation 
                                        stepBack={unsafeStepBack}
                                        nextStep={unsafeAdvanceStep}
                                        vendorInfo={initialInformation}
                                        cmsInfo={merchantInfo}
                                        setCmsInfo={setMerchantInfo}
                                        classes={classes} 
                                        nexioTokenGenerated={nexioTokenGenerated}
                                        setNexioTokenGenerated={setNexioTokenGenerated}
                                    />
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel>Final Approval</StepLabel>
                                <StepContent>
                                    <FinalApproval 
                                        stepBack={unsafeStepBack}
                                        finalApproval={finalApproval}
                                        setFinalApproval={setFinalApproval}
                                        vendorInfo={initialInformation}
                                        cmsInfo={merchantInfo}
                                    />
                                </StepContent>
                            </Step>
                        </Stepper>
                    </Paper>}
                </Grid>
            </Grid>
            <Backdrop open={loadingInfo} className={classes.backdrop}>
                <Grid container direction='column' justify='center' alignItems='center' spacing={4}>
                    <Grid item>
                        <Typography variant='h6'>Please wait...</Typography>
                    </Grid>
                    <Grid item>
                        <CircularProgress variant='indeterminate' size={60} />
                    </Grid>
                </Grid>
            </Backdrop>
        </div>
    );
}